import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Label } from "reactstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { Trans, useTranslation } from "react-i18next";
import { Button, Col, Row, Stack } from "react-bootstrap";
import { Card } from "@/CommonElements/Hajj/Card";
import {
  useForm,
  FormProvider,
  useFormContext,
  useWatch,
  Controller,
} from "react-hook-form";
import { useHouseExaminations } from "@/utils/hooks/useHouseExaminations";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { Spinner } from "reactstrap";
import { Alert, Flex, Input, Steps, Switch, Tag } from "antd";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { Link, useParams, useSearchParams } from "react-router";
import {
  useDeleteHouseExaminationItemImageMutation,
  useOneBuildingItemsQuery,
  useUpdateOrderItemsMutation,
  useUploadHouseExaminationItemMutation,
} from "@/services/houseInspections";
import { handleFormErrors, PickKeyPairs } from "@/utils/helpers";
import { LabelValue } from "@/Component/Value";
import {
  numbersOnlyRules,
  saudiMobileRules,
  validateImage,
} from "@/Pages/ProfileForm";
import { AntInput } from "@/CommonElements/AntInput";
import { Signature } from "@/Component/Signature/Signature";
import { DATE_TIME_FORMAT_12_HOURS } from "@/utils/constants";
import { AntSekelton } from "@/Component/Skeleton";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { UploadInput } from "@/Component/UploadInput";
import { useFile } from "@/utils/hooks/useFile";
import { useSystem } from "@/utils/hooks/useLocation";

export const HousingInspectionExamination = () => {
  // ==================
  // Hooks
  // ==================
  const [currentStep, setCurrentStep] = useState(0);
  const { order_number, house_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { handleUrlToFile } = useFile();
  const form = useForm({
    defaultValues: {
      save_result: "1",
    },
  });
  const { data: housingInspectionOrder, isLoading: isLoadingOrder } =
    useOneBuildingItemsQuery({ enabled: true });
  const order = useMemo(
    () => housingInspectionOrder?.data?.data,
    [housingInspectionOrder]
  );

  const { mutate: updateOrderItems, isPending: isUpdatingOrderItems } =
    useUpdateOrderItemsMutation();

  // Ref to access signature canvas refs
  const signatureRefs = useRef({ owner: null, assigned_user: null });

  const isQualified = !!order?.examination_result;

  const hasResult = searchParams.get("has_result");

  if (!hasResult && !isLoadingOrder) {
    setSearchParams({
      has_result: isQualified,
    });
  }
  useEffect(() => {
    const payload = order?.items?.map((item) => {
      const picked =
        PickKeyPairs(item, [
          "notes",
          "house_examination_item_id",
          "item_status",
          "checked",
          "id",
          "images",
        ]) || {};

      return {
        ...picked,
        item_status:
          !+picked.checked && !!item.images.length
            ? "notAvailable"
            : !+picked.checked
              ? "available"
              : picked.item_status.value,
        checked: "1",
      };
    });

    form.reset({
      items:
        payload?.map((item) => ({
          ...item,
        })) || [],
      save_result: "1",
      OwnerName: order?.house?.OwnerName,
      REP_MOBILE_NO: order?.house?.REP_MOBILE_NO,
    });
  }, [order, order_number]);
  // ==================
  // Handlers
  // ==================
  const onSubmit = async (data) => {
    const hasImagesUploadedBefore = data.items.some(
      (item) => !!item.images.length
    );
    const image_included = data.items.some((item) => !!item.images.length);
    const include_images = hasImagesUploadedBefore || image_included;
    const formData = new FormData();
    data.items = data.items.map((item) => ({
      ...item,
      house_id: house_id,
    }));

    Object.entries(data).forEach(([key, val]) => {
      // Handle File/Blob objects (like signature images)
      if (val instanceof File || val instanceof Blob) {
        formData.append(key, val);
      }
      // Handle arrays and plain objects
      else if (typeof val === "object" && val !== null) {
        formData.append(key, JSON.stringify(val));
      }
      // Handle primitives (strings, numbers, booleans)
      else {
        formData.append(key, val);
      }
    });

    updateOrderItems(
      { formData, include_images },
      {
        onError: (error) => {
          handleFormErrors(
            error,
            form.setError,
            t,
            "HOUSING_INSPECTION_ORDER_ITEMS_UPDATED_FAILED"
          );
        },
      }
    );
  };
  const handleFormSubmit = (e) => {
    e.preventDefault();
    form.handleSubmit(onSubmit)(e);
  };

  const { currentSystem } = useSystem();

  return (
    <div>
      <PageHeader title={t("HOUSING_INSPECTION_ITEMS")} sticky={false} />
      <Container fluid>
        <FormProvider {...form}>
          <form onSubmit={handleFormSubmit}>
            <Row>
              <Col xs={12} lg={8}>
                {isLoadingOrder ? (
                  <Flex>
                    <AntSekelton.Node
                      className="flex-fill"
                      style={{
                        width: "100%",
                        minWidth: "100px",
                        height: "300px",
                      }}
                    />
                  </Flex>
                ) : (
                  // <HousingInspectionExaminationForm />
                  <Stepper
                    isQualified={isQualified}
                    currentStep={currentStep}
                    signatureRefs={signatureRefs}
                  />
                )}
              </Col>
              <Col xs={12} lg={4}>
                <Stack gap={2}>
                  <ExaminationOrderDetails />
                  {order?.examination_result ? <ExaminationResult /> : null}
                </Stack>
              </Col>
            </Row>

            {!isQualified && !isLoadingOrder && (
              <ActionsFooter>
                {currentStep === 1 && (
                  <Button
                    key={"submit"}
                    className="btn btn-primary"
                    type="submit"
                    disabled={isUpdatingOrderItems}
                  >
                    {isUpdatingOrderItems && <Spinner size="sm" />}
                    {t("SAVE")}
                  </Button>
                )}
                {currentStep < 1 && (
                  <Button
                    key={"continue"}
                    className="btn btn-primary"
                    type="button"
                    onClick={() => {
                      setCurrentStep((prev) => Math.min(prev + 1, 1));
                    }}
                  >
                    {t("CONTINUE")}
                  </Button>
                )}

                <Button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setCurrentStep(Math.max(currentStep - 1, 0))}
                  disabled={currentStep <= 0 || isUpdatingOrderItems}
                >
                  {t("BACK")}
                </Button>
                <Link
                  disabled={isUpdatingOrderItems}
                  style={{
                    pointerEvents: isUpdatingOrderItems ? "none" : "auto",
                  }}
                  to={
                    isUpdatingOrderItems
                      ? undefined
                      : window.history.length > 1
                        ? -1
                        : `/app/${currentSystem}/housing-inspection`
                  }
                  className="btn btn-danger"
                  type="button"
                >
                  {t("CANCEL")}
                </Link>
              </ActionsFooter>
            )}
          </form>
        </FormProvider>
      </Container>
    </div>
  );
};
const { TextArea } = Input;
const Item = (props) => {
  const {
    mutate: uploadHouseExaminationItem,
    isPending: isUploadingHouseExaminationItem,
  } = useUploadHouseExaminationItemMutation();
  const {
    mutate: deleteHouseExaminationItemImage,
    isPending: isDeletingHouseExaminationItemImage,
  } = useDeleteHouseExaminationItemImageMutation();
  const { children, index, isQualified, item } = props;
  const { t } = useTranslation();
  const form = useFormContext();
  const images = useWatch({
    control: form.control,
    name: `items.${index}.images`,
  });
  const itemStatus = useWatch({
    control: form.control,
    name: `items.${index}.item_status`,
  });

  return (
    <Stack gap={2} className="border rounded p-2">
      <Row>
        <Col xs={9}>
          <span>{children}</span>
        </Col>

        {/* switch */}
        <Col
          xs={3}
          className="text-end align-items-start"
          style={{ position: "relative" }}
        >
          <Controller
            control={form.control}
            name={`items.${index}.item_status`}
            render={({ field }) => (
              <Switch
                disabled={isQualified || !!images?.length}
                style={{
                  position: "absolute",
                  top: 0,
                  left: "10px",
                  backgroundColor:
                    field.value === "available" ? "green" : "red",
                  width: "80px",
                }}
                {...field}
                onChange={(checked) => {
                  field.onChange(checked ? "available" : "notAvailable");
                }}
                checked={itemStatus === "available"}
                checkedChildren={t("AVAILABLE")}
                unCheckedChildren={t("NOT_AVAILABLE")}
                size="default"
              />
            )}
          />
        </Col>
      </Row>
      {itemStatus !== "available" && (
        <div>
          <Controller
            control={form.control}
            name={`items.${index}.notes`}
            render={({ field }) => (
              <Label className="fw-bold w-100">
                <div className="mb-2">{t("NOTES")}</div>
                <TextArea
                  className="w-100"
                  {...field}
                  placeholder={t("NOTES")}
                  disabled={isQualified}
                />
              </Label>
            )}
          />

          {/* <<<<< new feature >>>>> */}
          {!isQualified && (
            <Stack gap={2}>
              <Alert message={t("FILE_SIZE_LIMIT_2MB")} type="info" showIcon />
              <Alert message={t("TOTAL_IMAGES_LIMIT_3")} type="info" showIcon />
            </Stack>
          )}
          <Controller
            control={form.control}
            name={`items.${index}.images`}
            render={({ field }) => (
              <Stack gap={2}>
                <Label className="fw-bold">{t("UPLOAD_FILES")}</Label>
                <UploadInput
                  disabled={
                    isUploadingHouseExaminationItem ||
                    isDeletingHouseExaminationItemImage
                  }
                  hideUpload={isQualified}
                  maxCount={
                    3 -
                    (field.value?.filter((image) => !!image?.id).length || 0)
                  }
                  multiple
                  onChange={(files, info) => {
                    field.onChange(files);
                  }}
                  value={field.value}
                  beforeUpload={(file) => {
                    const isValid = validateImage(file);

                    if (!isValid)
                      form.setError(`items.${index}.images`, {
                        message: t("FILE_SIZE_LIMIT_2MB"),
                      });

                    return isValid;
                  }}
                  onDelete={(image) => {
                    field.onChange(
                      deleteHouseExaminationItemImage(
                        { item_id: item.id, id: image.id },
                        {
                          onSuccess: () => {
                            field.onChange(
                              field.value.filter((i) => i.id !== image.id)
                            );
                          },
                        }
                      )
                    );
                  }}
                />

                {!(field.value.filter((image) => image?.id).length >= 3) &&
                  !isQualified && (
                    <div>
                      <Button
                        disabled={
                          isUploadingHouseExaminationItem ||
                          isDeletingHouseExaminationItemImage
                        }
                        type="button"
                        onClick={() => {
                          const images = form.getValues(
                            `items.${index}.images`
                          );
                          uploadHouseExaminationItem(
                            {
                              item_id: item.id,
                              images,
                            },
                            {
                              onSuccess: (data) => {
                                const images = data.data.data.images;

                                form.setValue(`items.${index}.images`, images);
                              },
                            }
                          );
                        }}
                        className="btn btn-outline btn-brand btn-sm"
                      >
                        {isUploadingHouseExaminationItem && (
                          <Spinner size="sm" />
                        )}
                        {t("UPLOAD_FILES")}
                      </Button>
                    </div>
                  )}
              </Stack>
            )}
          />
        </div>
      )}
    </Stack>
  );
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */

const HousingInspectionExaminationForm = ({ isQualified }) => {
  // ==================
  // Hooks
  // ==================
  const { t } = useTranslation();
  const form = useFormContext();
  const items = form.getValues("items");
  const { getLocalizedText } = useLocalizedText();
  const { houseExaminations } = useHouseExaminations();

  return (
    <Card
      header={<span className="H5">{t("HOUSING_INSPECTION_ITEMS")}</span>}
      className="mb-2"
    >
      {!isQualified && (
        <Label className="fw-bold mb-4 d-inline-flex flex-column">
          <div className="mb-2">{t("Change_All_Items_Status")}</div>
          <ChangeAllItemsStatus isQualified={isQualified} />
        </Label>
      )}

      <Stack gap={2}>
        {items?.map((item, index) => {
          const { name_ar, name_en } =
            houseExaminations?.examination_items?.find(
              (i) => i.id === item.house_examination_item_id
            ) || {};

          return (
            <Item
              key={item.id}
              index={index}
              item={item}
              isQualified={isQualified}
            >
              {getLocalizedText(name_ar, name_en)}
            </Item>
          );
        })}
      </Stack>
    </Card>
  );
};
const V_Value = ({ colSize, ...props }) => {
  const { isLoading } = useOneBuildingItemsQuery({ enabled: false });

  return (
    <Col {...{ xs: 6, ...(colSize || {}) }} className="mb-2">
      <LabelValue loading={isLoading} direction="vertical" {...props} />
    </Col>
  );
};
const Stepper = ({ currentStep, signatureRefs, isQualified }) => {
  const { t } = useTranslation();
  const items = [
    {
      title: t("INSPECTION_ITEMS"),
    },
    {
      title: t("SUBMIT"),
    },
  ];

  if (isQualified) {
    return <HousingInspectionExaminationForm isQualified={isQualified} />;
  }

  return (
    <div className="d-flex flex-column gap-4">
      <Steps current={currentStep} items={items} />
      {currentStep === 0 && (
        <HousingInspectionExaminationForm isQualified={isQualified} />
      )}
      {currentStep === 1 && (
        <ReformDeadlineInput
          signatureRefs={signatureRefs}
          isQualified={isQualified}
        />
      )}
    </div>
  );
};
const ReformDeadlineInput = ({ signatureRefs, isQualified }) => {
  const { t } = useTranslation();
  const form = useFormContext();
  const items = useWatch({
    control: form.control,
    name: `items`,
  });

  const isAllItemsAvailable = items.every(
    (item) => item.item_status === "available"
  );

  return (
    <Card header={<span className="H5">{t("SUBMIT")}</span>} className="mb-2">
      <Stack gap={2}>
        {!isAllItemsAvailable && (
          <Label className="flex-fill">
            <div className="required">{t("REFORM_DEADLINE")}</div>
            <Controller
              rules={{
                required: t("FIELD_REQUIRED"),
                ...numbersOnlyRules(t),
                maxLength: {
                  value: 2,
                  message: t("REFORM_DEADLINE_MAX_LENGTH"),
                }
              }}
              control={form.control}
              name={`reform_deadline`}
              render={({ field }) => (
                <>
                  <AntInput
                    disabled={isQualified}
                    status={
                      form.formState.errors.reform_deadline
                        ? "error"
                        : undefined
                    }
                    {...field}
                    placeholder={t("REFORM_DEADLINE")}
                  />
                  {form.formState.errors.reform_deadline && (
                    <div className="text-danger mt-1">
                      {form.formState.errors.reform_deadline.message}
                    </div>
                  )}
                </>
              )}
            />
          </Label>
        )}

        <Label className="flex-fill">
          <div className="required">{t("اسم مفوض الفندق")}</div>
          <Controller
            rules={{
              required: t("FIELD_REQUIRED"),
            }}
            control={form.control}
            name={`OwnerName`}
            render={({ field }) => (
              <>
                <AntInput
                  disabled={isQualified}
                  status={form.formState.errors.OwnerName ? "error" : undefined}
                  {...field}
                  placeholder={t("اسم مفوض الفندق")}
                />
                {form.formState.errors.OwnerName && (
                  <div className="text-danger mt-1">
                    {form.formState.errors.OwnerName.message}
                  </div>
                )}
              </>
            )}
          />
        </Label>
        <Label className="flex-fill">
          <div className="required">{t("REP_MOBILE_NO")}</div>
          <Controller
            rules={{
              required: t("FIELD_REQUIRED"),
              ...saudiMobileRules(t),
            }}
            control={form.control}
            name={`REP_MOBILE_NO`}
            render={({ field }) => (
              <>
                <AntInput
                  disabled={isQualified}
                  status={
                    form.formState.errors.REP_MOBILE_NO ? "error" : undefined
                  }
                  {...field}
                  placeholder={t("REFORM_DEADLINE")}
                />
                {form.formState.errors.REP_MOBILE_NO && (
                  <div className="text-danger mt-1">
                    {form.formState.errors.REP_MOBILE_NO.message}
                  </div>
                )}
              </>
            )}
          />
        </Label>

        <SignatureComponent
          signatureRefs={signatureRefs}
          isQualified={isQualified}
        />
      </Stack>
    </Card>
  );
};
const SignatureComponent = ({ signatureRefs, isQualified }) => {
  const form = useFormContext();
  const { t } = useTranslation();
  const ownerCanvasRef = useRef(null);
  const assignedUserCanvasRef = useRef(null);

  // Store refs in parent's ref object
  React.useEffect(() => {
    if (signatureRefs) {
      signatureRefs.current.owner = ownerCanvasRef.current;
      signatureRefs.current.assigned_user = assignedUserCanvasRef.current;
    }
  }, [signatureRefs]);

  return (
    <Flex align="center" justify="space-between" wrap gap={2}>
      <div className="mx-auto">
        <Label className="d-block fw-bold required">
          {t("HOTEL_MANAGER_SIGNATURE")}
        </Label>
        <Controller
          rules={{
            required: t("FIELD_REQUIRED"),
          }}
          control={form.control}
          name="house_owner_signature"
          render={({ field }) => (
            <Signature
              disabled={isQualified}
              onChange={(value) => {
                field.onChange(value);
              }}
              value={field.value}
              error={form.formState.errors.house_owner_signature?.message}
            />
          )}
        />
      </div>
      <div className="mx-auto">
        <Label className="d-block fw-bold required">{t("عضو الوردية")}</Label>

        <Controller
          rules={{
            required: t("FIELD_REQUIRED"),
          }}
          control={form.control}
          name="employee_signature"
          render={({ field }) => (
            <Signature
              disabled={isQualified}
              onChange={(value) => {
                field.onChange(value);
              }}
              value={field.value}
              error={form.formState.errors.employee_signature?.message}
            />
          )}
        />
      </div>
    </Flex>
  );
};
const ExaminationOrderDetails = () => {
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();
  const { data: housingInspectionOrder, isLoading: isLoadingOrder } =
    useOneBuildingItemsQuery({ enabled: false });
  const order = useMemo(
    () => housingInspectionOrder?.data?.data,
    [housingInspectionOrder]
  );

  return (
    <Card
      header={<span className="H5">{t("HOUSING_INSPECTION_ORDER_DATA")}</span>}
    >
      <Row>
        <V_Value
          direction="vertical"
          label={t("HOUSING_INSPECTION_ORDER_TYPE")}
          value={getLocalizedText(
            order?.order?.examination_type?.label_ar,
            order?.order?.examination_type?.label_en
          )}
        />

        <V_Value
          direction="vertical"
          label={t("HOUSING_INSPECTION_ORDER_DATE")}
          value={
            order?.order?.inspection_date
              ? getLocalizedDate(
                order?.order?.inspection_date,
                DATE_TIME_FORMAT_12_HOURS
              )
              : "-"
          }
        />
        <V_Value
          direction="vertical"
          label={t("HOUSING_INSPECTION_ORDER_MEMBER_1")}
          value={getLocalizedText(
            order?.order?.assigned_users?.[0]?.name_ar,
            order?.order?.assigned_users?.[0]?.name_en
          )}
        />

        <V_Value
          direction="vertical"
          label={t("HOUSING_INSPECTION_ORDER_MEMBER_2")}
          value={getLocalizedText(
            order?.order?.assigned_users?.[1]?.name_ar,
            order?.order?.assigned_users?.[1]?.name_en
          )}
        />

        <V_Value
          direction="vertical"
          label={t("HOUSING_INSPECTION_ORDER_PERIOD")}
          value={getLocalizedText(
            order?.order?.examination_period?.name_ar,
            order?.order?.examination_period?.name_en
          )}
        />

        <V_Value
          direction="vertical"
          label={t("BUILDING_NAME")}
          value={order?.house?.houseCommercialNameAr || "-"}
        />

        {/* new */}
        <V_Value
          direction="vertical"
          label={t("ORDER_INSPECTION_NUMBER")}
          value={order?.house?.house_order_number || "-"}
        />
        <V_Value
          direction="vertical"
          label={t("HOUSING_INSPECTION_INSPECTION_COUNT")}
          value={order?.house?.examination_count || "-"}
        />
      </Row>
    </Card>
  );
};
const ExaminationResult = () => {
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedText } = useLocalizedText();
  const { data: housingInspectionOrder, isLoading } = useOneBuildingItemsQuery({
    enabled: false,
  });
  const examination_result = useMemo(
    () => housingInspectionOrder?.data?.data?.examination_result || {},
    [housingInspectionOrder]
  );

  const {
    examined_at = null,
    reform_deadline = null,
    OwnerName = null,
    REP_MOBILE_NO = null,
    employee_signature = null,
    house_owner_signature = null,
    status = null,
  } = examination_result;

  return (
    <Card
      header={
        isLoading ? (
          <AntSekelton.Text />
        ) : (
          <>
            <span className="H5">{t("EXAMINATION_RESULT")}</span>
            {(status && (
              <Tag color={status.value === "qualified" ? "green" : "red"}>
                {getLocalizedText(status.label_ar, status.label_en)}
              </Tag>
            )) || <Tag>-</Tag>}
          </>
        )
      }
      className="mb-2"
    >
      <Row>
        <V_Value
          label={t("EXAMINED_AT")}
          value={
            !!examined_at
              ? getLocalizedDate(examined_at, DATE_TIME_FORMAT_12_HOURS)
              : "-"
          }
        />
        <V_Value label={t("REFORM_DEADLINE")} value={reform_deadline || "-"} />
        <V_Value label={t("HOTEL_MANAGER_NAME")} value={OwnerName || "-"} />
        <V_Value label={t("REP_MOBILE_NO")} value={REP_MOBILE_NO || "-"} />

        <Flex align="center" justify="space-between" wrap gap={8}>
          <Stack gap={2} className="mx-auto">
            <V_Value
              label={t("HOTEL_MANAGER_SIGNATURE")}
              renderValue={() => (
                <RenderSignature
                  src={house_owner_signature?.url}
                  alt={t("HOTEL_MANAGER_SIGNATURE")}
                />
              )}
            />
          </Stack>
          <Stack gap={2} className="mx-auto">
            <V_Value
              label={t("MEMBER_SIGNATURE")}
              renderValue={() => (
                <RenderSignature
                  src={employee_signature?.url}
                  alt={t("EMPLOYEE_SIGNATURE")}
                />
              )}
            />
          </Stack>
        </Flex>
      </Row>
    </Card>
  );
};
const RenderSignature = (props) => {
  const { isLoading } = useOneBuildingItemsQuery({ enabled: false });

  if (isLoading) {
    return <AntSekelton.Image />;
  }

  return <img className="border rounded-3" {...props} />;
};
const ChangeAllItemsStatus = ({ isQualified }) => {
  const { confirm, ModalComponent } = useConfirm();
  const { t } = useTranslation();
  const form = useFormContext();
  const items = useWatch({
    control: form.control,
    name: "items",
  });

  const isAllItemsAvailable = items?.every(
    (item) => item.item_status === "available"
  );

  const handleChangeAllItemsStatus = (checked) => {
    confirm({
      title: t("CONFIRM"),
      content: (
        <Trans
          i18nKey="CONFIRM_CHANGE_ALL_ITEMS_STATUS"
          components={{ danger: <span className="text-danger" /> }}
        />
      ),
      onOk: () => {
        handleConfirm(checked);
      },
      onCancel: () => { },
    });
  };

  const handleConfirm = (checked) => {
    form.setValue(
      "items",
      items.map((item) => ({
        ...item,
        item_status: checked ? "available" : "notAvailable",
      }))
    );
  };

  return (
    <>
      <ModalComponent />
      <Switch
        disabled={isQualified}
        style={{
          backgroundColor: isAllItemsAvailable ? "green" : "red",
        }}
        checked={isAllItemsAvailable}
        checkedChildren={t("AVAILABLE")}
        unCheckedChildren={t("NOT_AVAILABLE")}
        defaultChecked={isAllItemsAvailable}
        onChange={(checked) => {
          handleChangeAllItemsStatus(checked);
        }}
      />
    </>
  );
};
